/**
 * Layer Strategy Pattern Interface
 *
 * This interface defines the contract for layer-specific processing logic.
 * Each layer (Stars, Looks, Worlds, Moves, Songs, Composite) implements this interface
 * to provide consistent, layer-aware functionality.
 *
 * Phase 1 Implementation: Safe foundation without breaking existing code
 */

import {
  validateFileForLayer,
  getLayerFileTypeDescription,
  getLayerAcceptAttribute,
  layerSupportsMultipleFiles,
} from './LayerFileTypes';

export interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
  warnings: ValidationWarning[];
}

export interface ValidationError {
  field: string;
  type: 'required' | 'type' | 'format' | 'dependency';
  message: string;
  severity: 'error' | 'warning';
}

export interface ValidationWarning {
  field: string;
  message: string;
  suggestion?: string;
}

export interface ProcessedMetadata {
  normalized: any;
  enhanced: any;
  tags: string[];
  confidence: number;
  processingMethod: string;
}

export interface LayerDefaults {
  [key: string]: any;
}

export interface LayerFieldConfig {
  required: string[];
  optional: string[];
  computed: string[];
  inherited: string[];
}

/**
 * Core Layer Strategy Interface
 */
export interface LayerStrategy {
  // Layer identification
  readonly layerCode: string;
  readonly layerName: string;
  readonly layerDescription: string;

  // Metadata validation
  validateMetadata(metadata: any): ValidationResult;
  getRequiredFields(): string[];
  getOptionalFields(): string[];
  getFieldConfig(): LayerFieldConfig;

  // File validation
  validateFile(file: File): { valid: boolean; error?: string };
  getAcceptedFileTypes(): string;
  getFileTypeDescription(): string;
  supportsMultipleFiles(): boolean;

  // AI response processing
  processAIResponse(response: any): ProcessedMetadata;
  extractMetadataFromAI(aiResponse: any): any;
  enhanceWithDefaults(metadata: any): any;

  // Data transformation
  sanitizeForBackend(metadata: any): any;
  normalizeMetadata(metadata: any): any;
  applyDefaults(metadata: any): any;

  // Inheritance and relationships
  applyInheritance(metadata: any, baseMetadata?: any): any;
  getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  };

  // Tag generation
  generateTags(metadata: any, description?: string): string[];
  categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  };

  // Layer-specific business logic
  validateBusinessRules(metadata: any): ValidationResult;
  getDefaultMetadata(): LayerDefaults;
}

/**
 * Abstract Base Layer Strategy
 * Provides common functionality for all layer implementations
 */
export abstract class BaseLayerStrategy implements LayerStrategy {
  abstract readonly layerCode: string;
  abstract readonly layerName: string;
  abstract readonly layerDescription: string;

  // File validation implementation using LayerFileTypes
  validateFile(file: File): { valid: boolean; error?: string } {
    return validateFileForLayer(file, this.layerCode as any);
  }

  getAcceptedFileTypes(): string {
    return getLayerAcceptAttribute(this.layerCode as any);
  }

  getFileTypeDescription(): string {
    return getLayerFileTypeDescription(this.layerCode as any);
  }

  supportsMultipleFiles(): boolean {
    return layerSupportsMultipleFiles(this.layerCode as any);
  }

  // Common validation logic
  validateMetadata(metadata: any): ValidationResult {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    // Check required fields
    const requiredFields = this.getRequiredFields();
    requiredFields.forEach(field => {
      if (
        !metadata[field] ||
        (Array.isArray(metadata[field]) && metadata[field].length === 0) ||
        (typeof metadata[field] === 'string' && metadata[field].trim() === '')
      ) {
        errors.push({
          field,
          type: 'required',
          message: `${field} is required for ${this.layerName} layer`,
          severity: 'error',
        });
      }
    });

    // Validate business rules
    const businessValidation = this.validateBusinessRules(metadata);
    errors.push(...businessValidation.errors);
    warnings.push(...businessValidation.warnings);

    return {
      isValid: errors.filter(e => e.severity === 'error').length === 0,
      errors,
      warnings,
    };
  }

  // Common AI response processing
  processAIResponse(response: any): ProcessedMetadata {
    const extracted = this.extractMetadataFromAI(response);
    const normalized = this.normalizeMetadata(extracted);
    const enhanced = this.enhanceWithDefaults(normalized);
    const tags = this.generateTags(enhanced, response.description);

    return {
      normalized,
      enhanced,
      tags,
      confidence: response.confidence || 0.8,
      processingMethod: response.processingMethod || 'hybrid-ai-architecture',
    };
  }

  // Common data sanitization
  sanitizeForBackend(metadata: any): any {
    const sanitized = { ...metadata };

    // Remove null/undefined values
    Object.keys(sanitized).forEach(key => {
      if (sanitized[key] === null || sanitized[key] === undefined) {
        delete sanitized[key];
      }

      // Clean empty arrays
      if (Array.isArray(sanitized[key]) && sanitized[key].length === 0) {
        delete sanitized[key];
      }

      // Clean empty strings
      if (typeof sanitized[key] === 'string' && sanitized[key].trim() === '') {
        delete sanitized[key];
      }
    });

    return sanitized;
  }

  // Common normalization logic
  normalizeMetadata(metadata: any): any {
    const normalized = { ...metadata };

    // Normalize arrays
    Object.keys(normalized).forEach(key => {
      if (
        typeof normalized[key] === 'string' &&
        normalized[key].includes(',')
      ) {
        normalized[key] = normalized[key]
          .split(',')
          .map((item: string) => item.trim());
      }
    });

    return normalized;
  }

  // Common default application
  applyDefaults(metadata: any): any {
    const defaults = this.getDefaultMetadata();
    const result = { ...defaults, ...metadata };

    // Apply layer-specific default logic
    return result;
  }

  // Common inheritance logic
  applyInheritance(metadata: any, baseMetadata?: any): any {
    if (!baseMetadata) return metadata;

    const result = { ...metadata };
    const inheritanceRules = this.getInheritanceRules();

    Object.entries(inheritanceRules).forEach(([field, rule]) => {
      const currentValue = result[field];
      const baseValue = baseMetadata[field];

      switch (rule) {
        case 'always':
          if (baseValue !== undefined) {
            result[field] = baseValue;
          }
          break;
        case 'if_empty':
          if (!currentValue && baseValue !== undefined) {
            result[field] = baseValue;
          }
          break;
        case 'merge':
          if (Array.isArray(currentValue) && Array.isArray(baseValue)) {
            result[field] = Array.from(
              new Set([...baseValue, ...currentValue])
            );
          } else if (
            typeof currentValue === 'object' &&
            typeof baseValue === 'object'
          ) {
            result[field] = { ...baseValue, ...currentValue };
          }
          break;
        case 'never':
        default:
          // Keep current value
          break;
      }
    });

    return result;
  }

  // Abstract methods that must be implemented by each layer
  abstract getRequiredFields(): string[];
  abstract getOptionalFields(): string[];
  abstract getFieldConfig(): LayerFieldConfig;
  abstract extractMetadataFromAI(aiResponse: any): any;
  abstract enhanceWithDefaults(metadata: any): any;
  abstract generateTags(metadata: any, description?: string): string[];
  abstract categorizeTags(tags: string[]): {
    primary: string[];
    secondary: string[];
    descriptive: string[];
  };
  abstract validateBusinessRules(metadata: any): ValidationResult;
  abstract getDefaultMetadata(): LayerDefaults;
  abstract getInheritanceRules(): {
    [field: string]: 'always' | 'if_empty' | 'never' | 'merge';
  };
}

/**
 * Layer Strategy Factory
 * Creates appropriate strategy instance based on layer code
 */
export class LayerStrategyFactory {
  private static strategies: Map<string, LayerStrategy> = new Map();

  static registerStrategy(layerCode: string, strategy: LayerStrategy): void {
    this.strategies.set(layerCode, strategy);
  }

  static getStrategy(layerCode: string): LayerStrategy {
    const strategy = this.strategies.get(layerCode);
    if (!strategy) {
      throw new Error(`No strategy registered for layer: ${layerCode}`);
    }
    return strategy;
  }

  static getAllStrategies(): Map<string, LayerStrategy> {
    return new Map(this.strategies);
  }

  static getSupportedLayers(): string[] {
    return Array.from(this.strategies.keys());
  }
}

/**
 * Layer Strategy Hook
 * React hook for using layer strategies in components
 */
export const useLayerStrategy = (layerCode: string): LayerStrategy => {
  return LayerStrategyFactory.getStrategy(layerCode);
};

export default LayerStrategy;
